import { Option } from '@modules/common/components/MultiColumnAutoComplete/Option';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Divider,
  Grid,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Dictionary, isEmpty } from 'lodash';
import { Fragment, ReactNode, SyntheticEvent } from 'react';
import { useMultiColumnAutoCompleteStore } from './hooks';
import { shouldRenderCustomGroup } from './utils';

interface MultiColumnDropdownProps<T> {
  sx?: SxProps<Theme>;
  groupBy?: (option: T) => string;
  optionsByGroup: Dictionary<[T]>;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    value: T | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T> | undefined,
  ) => void;
  renderGroup?: (option: T) => ReactNode;
}

export function MultiColumnDropdown<T extends { id: string }>({
  sx,
  optionsByGroup,
  onChange,
  groupBy,
  renderGroup,
}: MultiColumnDropdownProps<T>) {
  const groupsByRowColumn = useMultiColumnAutoCompleteStore(
    ({ groupsByRowColumn }) => groupsByRowColumn,
  );
  const optionIndexByOptionID = useMultiColumnAutoCompleteStore(
    ({ optionIndexByOptionID }) => optionIndexByOptionID,
  );

  if (!groupsByRowColumn.length || isEmpty(optionsByGroup)) {
    return null;
  }

  return (
    <Stack sx={sx} pt={1}>
      {groupsByRowColumn.map((row, rowIndex) => {
        return (
          <Fragment key={rowIndex}>
            <Grid container>
              {row.map((column, columnIndex) => {
                return (
                  <Grid key={columnIndex} item xs={12 / row.length}>
                    <Stack direction="column" spacing={2}>
                      {column.map((groupID) => {
                        if (!optionsByGroup[groupID]) {
                          return null;
                        }

                        const firstOption = optionsByGroup[groupID]?.[0];
                        const groupDisplayName =
                          firstOption && groupBy
                            ? groupBy(firstOption)
                            : groupID;

                        const canRenderCustomGroup =
                          renderGroup && shouldRenderCustomGroup(groupID);

                        return (
                          <Stack key={groupID} direction="column">
                            {renderGroup && canRenderCustomGroup
                              ? renderGroup(firstOption)
                              : !!groupDisplayName && (
                                  <Typography
                                    pl={2}
                                    py={0.75}
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    {groupDisplayName}
                                  </Typography>
                                )}
                            {optionsByGroup[groupID].map((option) => {
                              return (
                                <Option
                                  key={option.id}
                                  option={option}
                                  index={optionIndexByOptionID[option.id]}
                                  rowIndex={rowIndex}
                                  columnIndex={columnIndex}
                                  onChange={onChange}
                                />
                              );
                            })}
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
            {rowIndex === groupsByRowColumn.length - 1 ? null : (
              <Divider light sx={{ my: 1 }} />
            )}
          </Fragment>
        );
      })}
    </Stack>
  );
}
